import { SearchIcon } from "lucide-react";
import { useState } from "react";

import { Input } from "../ui/input";
import { PluginCreateAction } from "./PluginCreateAction";
import { ManagePluginRows, ManageSkillRows, type ManagePluginRow } from "./PluginManageRows";
import { allPluginDetailMetadata } from "./pluginMetadata";
import { isPluginSkillEnabled, setPluginSkillsEnabled } from "./pluginPermissions";
import { pluginConnectionState, sortedPlugins, type ForgePlugin } from "./pluginsPage.logic";
import { usePluginConnections } from "./PluginConnectionsProvider";
import { useSettings, useUpdateSettings } from "~/hooks/useSettings";

type ManageTabId = "plugins" | "apps" | "mcps" | "skills" | "marketplace";

interface ManageTab {
  readonly id: ManageTabId;
  readonly label: string;
  readonly count: number;
}

function pluginRows(plugins: readonly ForgePlugin[]): readonly ManagePluginRow[] {
  const sorted = sortedPlugins(plugins);
  return allPluginDetailMetadata().map((metadata) => {
    const plugin = sorted.find((entry) => entry.id === metadata.id) ?? {
      id: metadata.id,
      name: metadata.title,
      description: metadata.appDescription,
      status: "pending",
      authReady: false,
      authReason: "not_installed",
      connectionKind: metadata.id === "github" ? "github" : "cmmdConnector",
    };
    return {
      metadata,
      plugin,
      enabled: true,
      connected: pluginConnectionState(plugin) === "connected",
    };
  });
}

export function PluginManagePage() {
  const [query, setQuery] = useState("");
  const [activeTabId, setActiveTabId] = useState<ManageTabId>("plugins");
  const { plugins } = usePluginConnections();
  const permissions = useSettings((settings) => settings.pluginSkillPermissions);
  const { updateSettings } = useUpdateSettings();
  const rows = pluginRows(plugins).map((row) => {
    const skillIds = row.metadata.skills.map((skill) => skill.id);
    return Object.assign({}, row, {
      enabled: skillIds.every((skillId) =>
        isPluginSkillEnabled(permissions, row.metadata.id, skillId),
      ),
    });
  });
  const normalizedQuery = query.trim().toLowerCase();
  const visibleRows = rows.filter((row) =>
    normalizedQuery
      ? `${row.metadata.title} ${row.metadata.appDescription} ${row.metadata.category}`
          .toLowerCase()
          .includes(normalizedQuery)
      : true,
  );
  const skillCount = allPluginDetailMetadata().reduce(
    (count, metadata) => count + metadata.skills.length,
    0,
  );
  const tabs: readonly ManageTab[] = [
    { id: "plugins", label: "Plugins", count: rows.length },
    { id: "apps", label: "Apps", count: rows.length },
    { id: "mcps", label: "MCPs", count: 0 },
    { id: "skills", label: "Skills", count: skillCount },
    { id: "marketplace", label: "Marketplace", count: 0 },
  ];
  const setPluginEnabled = (row: ManagePluginRow, enabled: boolean) => {
    updateSettings({
      pluginSkillPermissions: setPluginSkillsEnabled(
        permissions,
        row.metadata.id,
        row.metadata.skills.map((skill) => skill.id),
        enabled,
      ),
    });
  };
  const visibleSkills = allPluginDetailMetadata()
    .flatMap((metadata) =>
      metadata.skills.map((skill) => ({
        id: `${metadata.id}:${skill.id}`,
        metadata,
        skill,
      })),
    )
    .filter((entry) =>
      normalizedQuery
        ? `${entry.skill.name} ${entry.skill.description} ${entry.metadata.title}`
            .toLowerCase()
            .includes(normalizedQuery)
        : true,
    );

  return (
    <main className="flex h-[calc(100dvh-var(--forge-app-top-bar-height))] min-h-0 min-w-0 flex-1 flex-col overflow-auto bg-background">
      <PluginManageChrome />
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-5 py-6 sm:px-8 lg:px-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <ManageTabs tabs={tabs} activeTabId={activeTabId} onTabChange={setActiveTabId} />
          <ManageSearch query={query} onQueryChange={setQuery} />
        </div>
        {activeTabId === "skills" ? (
          <ManageSkillRows
            skills={visibleSkills}
            permissions={permissions}
            onSkillEnabledChange={(metadata, skillId, enabled) => {
              updateSettings({
                pluginSkillPermissions: setPluginSkillsEnabled(
                  permissions,
                  metadata.id,
                  [skillId],
                  enabled,
                ),
              });
            }}
          />
        ) : activeTabId === "mcps" || activeTabId === "marketplace" ? (
          <p className="py-10 text-sm text-muted-foreground">
            No {activeTabId === "mcps" ? "MCPs" : "marketplace plugins"} are available yet.
          </p>
        ) : (
          <ManagePluginRows rows={visibleRows} onPluginEnabledChange={setPluginEnabled} />
        )}
      </div>
    </main>
  );
}

function PluginManageChrome() {
  // The bar's crumb is already `Plugins / Manage`; this row keeps only the
  // action that has nowhere else to live.
  return (
    <div className="flex min-h-11 items-center justify-end gap-3 px-5 pt-3 sm:px-8 lg:px-10">
      <div className="flex items-center gap-1">
        <PluginCreateAction />
      </div>
    </div>
  );
}

interface ManageTabsProps {
  readonly tabs: readonly ManageTab[];
  readonly activeTabId: ManageTabId;
  readonly onTabChange: (tabId: ManageTabId) => void;
}

function ManageTabs({ tabs, activeTabId, onTabChange }: ManageTabsProps) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={
            tab.id === activeTabId
              ? "rounded-lg bg-muted px-3 py-1 text-sm font-medium text-foreground"
              : "rounded-lg px-3 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          }
        >
          {tab.label} <span className="text-muted-foreground">{tab.count}</span>
        </button>
      ))}
    </div>
  );
}

interface ManageSearchProps {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
}

function ManageSearch({ query, onQueryChange }: ManageSearchProps) {
  return (
    <div className="flex h-9 w-full items-center gap-2 rounded-lg border border-input bg-background px-3 shadow-sm md:w-64">
      <SearchIcon className="pointer-events-none size-4 shrink-0 text-muted-foreground" />
      <Input
        unstyled
        type="search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search plugins"
        className="h-full flex-1 text-sm text-foreground [&_[data-slot=input]]:h-full [&_[data-slot=input]]:p-0 [&_[data-slot=input]]:leading-normal"
      />
    </div>
  );
}
